/**
 * SensiFloatingWidget - Floating chat bubble that expands to full chat
 * Positioned in bottom-right corner of the screen.
 * When shouldInitiate is true (e.g. first logins, get-started not done), auto-opens after a short delay.
 */

import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { Icons } from '../../lib/Icons';
import SensiChat from './SensiChat';

const SensiFloatingWidget = forwardRef(({
  shouldInitiate = false,
  mode = 'normal',
  onGetStartedComplete,
  onDismissGetStarted,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [getStartedCompleted, setGetStartedCompleted] = useState(false);
  const autoOpenDone = useRef(false);

  // Pulse / "Sensi has a message" when shouldInitiate or on first load
  useEffect(() => {
    const delay = shouldInitiate ? 1500 : 3000;
    const timer = setTimeout(() => {
      setHasNewMessage(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [shouldInitiate]);

  // Auto-open when shouldInitiate (get-started flow)
  useEffect(() => {
    if (!shouldInitiate || autoOpenDone.current) return;
    const timer = setTimeout(() => {
      setIsOpen(true);
      autoOpenDone.current = true;
      setHasNewMessage(false);
    }, 1500);
    return () => clearTimeout(timer);
  }, [shouldInitiate]);

  const handleGetStartedComplete = () => {
    setGetStartedCompleted(true);
    onGetStartedComplete?.();
  };

  const handleDismissGetStarted = () => {
    onDismissGetStarted?.();
  };

  useImperativeHandle(ref, () => ({
    open: () => {
      setIsOpen(true);
      setHasNewMessage(false);
    },
  }), []);

  const handleToggle = () => {
    setIsAnimating(true);
    setIsOpen(!isOpen);
    setHasNewMessage(false);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const effectiveMode = getStartedCompleted ? 'normal' : mode;

  return (
    <>
      {/* Backdrop when open on mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Chat panel */}
      <div
        className={`fixed z-50 transition-all duration-300 ease-out ${
          isOpen 
            ? 'bottom-0 right-0 md:bottom-4 md:right-4 w-full md:w-[400px] h-[80vh] md:h-[600px] md:rounded-2xl shadow-2xl' 
            : 'bottom-4 right-4 w-16 h-16'
        }`}
      >
        {isOpen ? (
          <SensiChat
            isExpanded={true}
            onClose={() => setIsOpen(false)}
            mode={effectiveMode}
            onGetStartedComplete={handleGetStartedComplete}
            onDismissGetStarted={onDismissGetStarted ? handleDismissGetStarted : undefined}
          />
        ) : (
          // Floating button
          <button
            onClick={handleToggle}
            className={`w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/25 flex items-center justify-center text-white hover:scale-110 transition-transform relative group ${
              isAnimating ? 'scale-95' : ''
            }`}
          >
            {/* Sparkles icon */}
            <Icons.Sparkles className="w-7 h-7" />
            
            {/* Pulse ring for new message */}
            {hasNewMessage && (
              <>
                <span className="absolute inset-0 rounded-full bg-violet-400 animate-ping opacity-25" />
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-900 animate-pulse" />
              </>
            )}

            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <div className="bg-slate-800 text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg border border-slate-700/50">
                {shouldInitiate ? "Let's get you set up" : "Ask Sensi for help"}
                <div className="absolute -bottom-1 right-4 w-2 h-2 bg-slate-800 border-r border-b border-slate-700/50 transform rotate-45" />
              </div>
            </div>
          </button>
        )}
      </div>
    </>
  );
});

SensiFloatingWidget.displayName = 'SensiFloatingWidget';

export default SensiFloatingWidget;



